import Logo from "~/components/Logo"
import {
  articleBodyStyle,
  articleCompareImages,
  articleFaqBodyStyle,
  articleImageStyle,
  bold,
  faqGrid,
  faqH3Style,
  figCaptionStyle,
  h1Style,
  h1Wrap,
  h2Style,
  h3Style,
  headerTextStyle,
  imagePreviewBodyStyle,
  infoWrap,
  orderedList,
  stepSectionStyle,
  unorderedList,
} from "./article.css"
import { MetaFunction } from "@remix-run/node"
import StickyHeader from "~/components/StickyHeader"
import { page } from "~/shared/styles.css"
import LinkButton from "~/components/LinkButton"
import Link from "~/components/Link"
import Footer from "~/components/Footer"
import { vars } from "~/components/styles/theme.css"
import { P } from "~/components/articleComponents"

// SEO FIX: Title shortened from 69 to 52 chars (was over 60 char limit)
const TITLE = "Features of Cottage Gardens | English Garden Design"
const DESCRIPTION =
  "Explore defining cottage garden design features, from overflowing floral borders and natural materials to informal layouts and wildlife-friendly planting."
const URL =
  "https://rescapeai.com/garden-design-guide/cottage/features-of-cottage-gardens"
const IMAGE = "https://rescapeai.com/articles/cottage-garden-winding-pathways.webp"

export const meta: MetaFunction = () => {
  return [
    {
      title: TITLE,
    },
    {
      name: "description",
      content: DESCRIPTION,
    },
    {
      property: "og:title",
      content: TITLE,
    },
    {
      property: "og:description",
      content: DESCRIPTION,
    },
    {
      property: "og:image",
      content: IMAGE,
    },
    // SEO FIX: Added Twitter Card tags
    {
      name: "twitter:card",
      content: "summary_large_image",
    },
    {
      name: "twitter:title",
      content: TITLE,
    },
    {
      name: "twitter:description",
      content: DESCRIPTION,
    },
    {
      name: "twitter:image",
      content: IMAGE,
    },
    {
      tagName: "link",
      rel: "canonical",
      href: URL,
    },
    {
      "script:ld+json": {
        "@context": "https://schema.org",
        "@type": "Article",
        headline: TITLE,
        description: DESCRIPTION,
        author: {
          "@type": "Organization",
          name: "Rescape AI",
        },
        datePublished: "2025-10-22",
        dateModified: "2025-10-22",
        publisher: {
          "@type": "Organization",
          name: "Rescape AI",
          logo: {
            "@type": "ImageObject",
            url: "https://rescapeai.com/logo.webp",
          },
        },
        image: IMAGE,
        mainEntityOfPage: {
          "@type": "WebPage",
          "@id": URL,
        },
      },
    },
  ]
}

function Index() {
  return (
    <>
      <div
        className={h1Style}
        style={{
          textAlign: "center",
          backgroundImage: "url(/articles/cottage-garden-winding-pathways.webp)",
        }}
      >
        <div className={h1Wrap}>
          <Logo width="108px" />
          <h1 className={headerTextStyle}>Design Guide: Features of Cottage Gardens</h1>
        </div>
      </div>
      <StickyHeader status="SignUp" />

      <div className={page}>
        <section className={stepSectionStyle}>
          <h3 className={h3Style}>
            Features of Cottage Gardens: Exploring the Key Elements That Define
            Traditional English Garden Design
          </h3>
          <p className={articleBodyStyle}>
            Cottage gardens capture the essence of traditional English charm—overflowing
            with colour, texture, and life. Combining fragrant flowers, rustic materials,
            and informal design, they create spaces that feel both romantic and lived-in.
            Discover how to design a timeless cottage garden filled with beauty,
            abundance, and natural harmony throughout the seasons.
          </p>
          <p className={articleBodyStyle}>
            Below are the key features that define cottage garden design.
          </p>
        </section>
        <section className={stepSectionStyle}>
          <h3 className={h3Style}>Informal Layouts and Natural Flow</h3>
          <figure>
            <img
              className={articleImageStyle}
              src="/articles/cottage-garden-unstructured.webp"
              alt="Overflowing cottage garden borders with a curved pathway and bench"
            />
            <figcaption className={figCaptionStyle}>
              Overflowing boundaries and a curved pathway with a bench
            </figcaption>
          </figure>
          <p className={articleBodyStyle}>
            Cottage gardens avoid rigid structure, favouring soft curves and organic
            movement. Meandering paths, uneven borders, and plants spilling over edges
            create a sense of natural freedom. The design feels spontaneous yet carefully
            layered, blending structure with a touch of wildness. The image above was{" "}
            <Link href="/article/contemporary-garden-ideas-with-rescape-ai">
              created with AI
            </Link>{" "}
            using the photo of the users garden and their stylistic choices.{" "}
            <Link href="/hello/signup">Create your garden with AI here</Link>
          </p>
        </section>
        <section className={stepSectionStyle}>
          <h3 className={h3Style}>Abundant and Layered Planting</h3>
          <img
            className={articleImageStyle}
            src="/articles/cottage-garden-planting.jpg"
            alt="Densely planted cottage garden borders with layered perennials and annuals"
          />
          <p className={articleBodyStyle}>
            Abundance is the heart of every cottage garden. Borders are densely filled
            with flowering perennials, annuals, herbs, and climbers. Plants intermingle
            freely—tall spires like delphiniums rise above mounded geraniums, while ground
            covers weave between stepping stones. This layered effect creates depth,
            colour, and a sense of joyful disorder.
          </p>
        </section>
        <section className={stepSectionStyle}>
          <h3 className={h3Style}>Traditional Materials and Rustic Textures</h3>
          <img
            className={articleImageStyle}
            src="/articles/jacobean-bricks-in-a-circle-Westminster-Stone.avif"
            alt="Reclaimed Jacobean brick circle path with rustic stone in a cottage garden"
          />
          <p className={articleBodyStyle}>
            Natural, weathered materials define the look. Think reclaimed brick paths,
            timber fences, stone walls, and gravel walkways softened by self-seeded
            plants. Aged finishes and patina add authenticity, while simple wooden
            benches, wrought-iron gates, or terracotta pots enhance the garden's timeless
            appeal. Rescape AI knows this and will give you cottage garden ideas from an
            uploaded photo of your garden. <Link href="/hello/signup">Join Now</Link>
          </p>
        </section>
        <section className={stepSectionStyle}>
          <h3 className={h3Style}>Mix of Ornamentals, Herbs, and Edibles</h3>
          <img
            className={articleImageStyle}
            src="/articles/cottage-garden-plants.webp"
            alt="Cottage garden plants mixing ornamental flowers, herbs, and edibles together"
          />
          <p className={articleBodyStyle}>
            True to their origins as practical home gardens, cottage gardens mix
            decorative and useful plants. Fragrant herbs like thyme, sage, and rosemary
            sit beside colourful perennials and vegetables. Strawberries, rhubarb, and
            climbing beans blend seamlessly into borders, uniting beauty with
            productivity.
          </p>
        </section>
        <section className={stepSectionStyle}>
          <h3 className={h3Style}>Seasonal Colour and Year-Round Interest</h3>
          <img
            className={articleImageStyle}
            src="/articles/small-flowers.webp"
            alt="Small delicate cottage garden flowers providing seasonal colour and year-round interest"
          />
          <p className={articleBodyStyle}>
            Cottage gardens change beautifully through the seasons. Spring brings tulips
            and forget-me-nots, summer bursts with roses and lavender, and autumn glows
            with dahlias and asters. Even in winter, evergreens, seed heads, and textured
            stems keep the garden alive with quiet character.
          </p>
        </section>
        <section className={stepSectionStyle}>
          <h3 className={h3Style}>Climbers and Vertical Interest</h3>
          <img
            className={articleImageStyle}
            src="/articles/cottage-garden-climbing-plants.webp"
            alt="Climbing roses and honeysuckle on a cottage garden arch adding vertical interest"
          />
          <p className={articleBodyStyle}>
            Vertical elements play a key role in cottage design. Arches, trellises, and
            pergolas support climbing roses, honeysuckle, or clematis, adding height and
            fragrance. These vertical layers frame views and create romantic focal points
            throughout the garden.
          </p>
        </section>
        <section className={stepSectionStyle}>
          <h3 className={h3Style}>Wildlife-Friendly Planting</h3>
          <img
            className={articleImageStyle}
            src="/articles/butterfly.webp"
            alt="Butterfly on cottage garden flowers showing wildlife-friendly planting benefits"
          />
          <p className={articleBodyStyle}>
            Cottage gardens are havens for wildlife. Nectar-rich flowers attract bees and
            butterflies, while dense planting provides shelter for birds and insects.
            Small ponds, nesting boxes, and wild corners enhance biodiversity, turning the
            garden into a living ecosystem.
          </p>
        </section>
        <section className={stepSectionStyle}>
          <h3 className={h3Style}>Soft Colour Palettes and Scented Blooms</h3>
          <img
            className={articleImageStyle}
            src="/articles/lavender.webp"
            alt="Lavender in a cottage garden providing soft colour and scent"
          />
          <p className={articleBodyStyle}>
            The colour scheme is lush yet harmonious—pastel pinks, purples, blues, and
            whites dominate, punctuated by the occasional bold accent. Fragrance is
            equally important: roses, sweet peas, and lavender fill the air with scent,
            engaging all the senses.
          </p>
        </section>
        <section className={stepSectionStyle}>
          <h3 className={h3Style}>Charming Decorative Details</h3>
          <img
            className={articleImageStyle}
            src="/articles/bird-bath.webp"
            alt="Weathered stone birdbath as a decorative detail in a cottage garden"
          />
          <p className={articleBodyStyle}>
            Small touches add personality to cottage gardens. Weathered garden ornaments,
            rustic birdbaths, and vintage containers bring individuality. Nothing feels
            too polished; each detail contributes to the storybook atmosphere of comfort
            and nostalgia.
          </p>
        </section>
        <section className={stepSectionStyle}>
          <h3 className={h3Style}>Relaxed Maintenance and Natural Growth</h3>
          <img
            className={articleImageStyle}
            src="/articles/pruning.webp"
            alt="Light pruning in a cottage garden encouraging natural self-seeding growth"
          />
          <P>
            Cottage gardens thrive under a light touch. Instead of strict pruning or rigid
            control, plants are allowed to self-seed and mingle. Regular deadheading
            encourages blooms, while mulch keeps weeds down. This approach celebrates
            imperfection and natural beauty.
          </P>
        </section>
        <section className={stepSectionStyle}>
          <h2 className={h2Style}>Conclusion</h2>
          <p className={articleBodyStyle}>
            Cottage gardens celebrate the joy of abundance, imperfection, and connection
            to nature. With their informal layouts, overflowing borders, and timeless
            charm, they offer a perfect blend of beauty and practicality. Whether
            sprawling across a countryside plot or blooming in a small urban garden, the
            cottage style creates an inviting retreat filled with life, colour, and
            character.
          </p>
        </section>
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            marginTop: "2rem",
          }}
        >
          <LinkButton url="/hello/signup">Join Rescape AI</LinkButton>
        </div>
        <section className={stepSectionStyle}>
          <h2 className={h2Style}>FAQ's</h2>

          <div className={faqGrid}>
            <div>
              <h3 className={faqH3Style}>What defines a cottage garden design?</h3>
              <p
                className={articleFaqBodyStyle}
                style={{
                  color: vars.color.light3,
                  opacity: 0.8,
                }}
              >
                A cottage garden design is known for its informal layout, dense planting,
                and romantic charm. Unlike structured modern gardens, cottage-style
                gardens overflow with flowers, herbs, and climbing plants. The focus is on
                natural beauty, using soft curves, rustic materials, and abundant colour
                to create a relaxed, lived-in feel inspired by traditional English
                gardens.
              </p>
            </div>
            <div>
              <h3 className={faqH3Style}>
                How can I create a low-maintenance cottage garden?
              </h3>
              <p
                className={articleFaqBodyStyle}
                style={{
                  color: vars.color.light3,
                  opacity: 0.8,
                }}
              >
                To enjoy an easy-care cottage garden, choose hardy perennials and
                self-seeding plants like lavender, geraniums, foxgloves, and catmint.
                These low-maintenance cottage garden plants return each year with minimal
                effort. Mulching, grouping plants by water needs, and allowing natural
                growth patterns reduce upkeep while preserving the garden's lush, informal
                look.
              </p>
            </div>
            <div>
              <h3 className={faqH3Style}>
                What are the best flowers for a classic English cottage garden?
              </h3>
              <p
                className={articleFaqBodyStyle}
                style={{
                  color: vars.color.light3,
                  opacity: 0.8,
                }}
              >
                The most loved English cottage garden flowers include roses, delphiniums,
                hollyhocks, lavender, and sweet peas. Combine them with herbs such as
                thyme and sage for fragrance and texture. Mixing perennials with annuals
                ensures year-round interest and captures the layered abundance that
                defines classic cottage garden planting.
              </p>
            </div>
            <div>
              <h3 className={faqH3Style}>
                Can I design a cottage garden in a small space or urban setting?
              </h3>
              <p
                className={articleFaqBodyStyle}
                style={{
                  color: vars.color.light3,
                  opacity: 0.8,
                }}
              >
                Absolutely. Even in a small yard or balcony, you can create a small
                cottage garden look by using containers, raised beds, and vertical
                climbers. Add fragrant plants like lavender or climbing roses and let
                foliage spill naturally over edges. This compact cottage garden idea
                brings countryside charm to any urban space.
              </p>
            </div>
            <div>
              <h3 className={faqH3Style}>
                How can I make my cottage garden more wildlife-friendly?
              </h3>
              <p
                className={articleFaqBodyStyle}
                style={{
                  color: vars.color.light3,
                  opacity: 0.8,
                }}
              >
                Wildlife-friendly cottage gardens attract pollinators and birds with
                nectar-rich blooms like foxgloves, cosmos, and echinacea. Include water
                sources such as birdbaths or mini ponds, and avoid pesticides to support
                biodiversity. Dense planting and natural habitats create a thriving,
                eco-friendly garden that's both beautiful and sustainable.
              </p>
            </div>
            <div>
              <h3 className={faqH3Style}>
                What materials work best in cottage-style landscaping?
              </h3>
              <p
                className={articleFaqBodyStyle}
                style={{
                  color: vars.color.light3,
                  opacity: 0.8,
                }}
              >
                Cottage-style landscaping favours natural, timeworn materials. Use
                reclaimed brick or stone for pathways, timber or wrought-iron fences, and
                terracotta pots for planting. Weathered finishes and organic textures
                create warmth and authenticity, blending perfectly with the garden's soft
                planting and old-world atmosphere.
              </p>
            </div>
          </div>
        </section>
        <div style={{ display: "flex", justifyContent: "center", marginTop: "1rem" }}>
          <LinkButton url="/hello/signup">Join Rescape AI</LinkButton>
        </div>
        <Footer showLinks={false} />
      </div>
    </>
  )
}

export default Index
